import { Grid } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import AsyncSelect from "../../../../../../../../components/AsyncSelect";
import StyledFormModal from "../../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../../hooks/useSnackbar";
import { useCreatePatientSocialHistory } from "../../../../../../../../queries/history/social-history/use-create-patient-social-history";
import { useGetAllPatientSocialHistory } from "../../../../../../../../queries/history/social-history/use-get-all-patient-social-history";
import { useSocialHistoryQuestions } from "../../../../../../../../queries/history/social-history/use-get-social-history-questions";
import { useUpdatePatientSocialHistory } from "../../../../../../../../queries/history/social-history/use-update-patient-social-history";
import { GetPatientSocialHistorySocialHistoryTypeEnum } from "../../../../../../../../services";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../../../utils/utils";
import { SOCIAL_HISTORY_QUESTION_TYPES } from "../history-constant";
import DynamicForm, { Response } from "./DynamicForm";

interface Props {
  disclosureHost: DisclosureHost;
}

const EncounterSocialHistoryModal = ({ disclosureHost }: Props) => {
  const prefillData = disclosureHost?.data;
  const appointmentId = useSearchParams()[0].get("appointmentid") as string;
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { patientId } = useParams() as { patientId: any };
  const [pagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting] = useState<GridSortModel>([]);
  const [searchKey] = useDebounce<string>("");
  const { mutateAsync: createPatientSocialHistory } =
    useCreatePatientSocialHistory();
  const { mutateAsync: updatePatientSocialHistory } =
    useUpdatePatientSocialHistory();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: prefillData || {
      selectedSocialHistoryType: undefined,
    },
    onSubmit: () => {
      // Add your submit logic here
    },
  });

  const selectedCard = formik.values.selectedSocialHistoryType
    ? (formik.values.selectedSocialHistoryType as any)?.value
    : undefined;

  const resetForm = () => {
    formik.resetForm();
    // onClose("Encounter Social History");
  };

  const { data: patientSocialHistoryQuestionsList } =
    useGetAllPatientSocialHistory({
      patientUuid: patientId,
      socialHistoryType:
        selectedCard as GetPatientSocialHistorySocialHistoryTypeEnum,
      appointmentId: appointmentId ?? undefined,
      xTENANTID: getXtenantId() ?? "",
    });

  const { data: commonHistoryQuestionsList } = useSocialHistoryQuestions({
    socialHistoryType:
      selectedCard as GetPatientSocialHistorySocialHistoryTypeEnum,
    pagination,
    gridSorting,
    searchString: searchKey,
    xTENANTID: getXtenantId(),
  });
  const [formData, setFormData] = useState<Response[]>([]);

  useEffect(() => {
    setFormData(
      patientSocialHistoryQuestionsList?.data?.patientQuestionAnswers?.length
        ? patientSocialHistoryQuestionsList?.data?.patientQuestionAnswers
        : commonHistoryQuestionsList?.data
    );
  }, [patientSocialHistoryQuestionsList, commonHistoryQuestionsList]);

  function submitResponse() {
    const payload = {
      patientQuestionAnswerRequest: {
        patient: {
          [patientId]: `${patientId}`,
        },
        appointmentId: appointmentId ?? undefined,
        socialHistoryType: selectedCard ?? "",
        patientQuestionAnswers: formData,
      },
      xTENANTID: getXtenantId() ?? undefined,
    };
    // console.log("payload ", payload);

    showToastAndReset(
      patientSocialHistoryQuestionsList?.data?.patientQuestionAnswers?.length
        ? updatePatientSocialHistory(payload as any)
        : createPatientSocialHistory(payload as any)
    );
  }

  const showToastAndReset = (apiCall: Promise<any>) => {
    apiCall
      ?.then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        resetForm?.();
        setFormData([]);
        queryClient.invalidateQueries({
          queryKey: ["all-patient-social-history"],
        });
        queryClient.invalidateQueries({
          queryKey: ["all-patient-social-history-status"],
        });
        if (appointmentId) {
          queryClient.invalidateQueries({
            queryKey: [
              "all-patient-social-history-by-appointment-id",
              appointmentId,
            ],
          });
        }

        // navigate to the next section
        // skipFunction?.(currentIndex);
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  function handleClose() {
    disclosureHost.close();
  }

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={handleClose}
      dialogTitle="Social History"
      dialogAction={
        <StyledButtonV2
          label="Save Response"
          variant="contained"
          disabled={!selectedCard}
          onClick={() => submitResponse()}
        />
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "50vw",
          height: "70vh",
        },
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{
          pt: 2,
        }}
      >
        <Grid size={12}>
          <AsyncSelect
            name="selectedSocialHistoryType"
            handleBlur={formik.handleBlur}
            value={formik.values.selectedSocialHistoryType}
            onChange={(val) =>
              formik.setFieldValue("selectedSocialHistoryType", val)
            }
            label="Social History Type"
            options={SOCIAL_HISTORY_QUESTION_TYPES}
            placeholder="Select Social History Type"
          />
        </Grid>
        <Grid size={12}>
          <DynamicForm formData={formData} setFormData={setFormData} />
        </Grid>
      </Grid>
    </StyledFormModal>
  );
};
export default EncounterSocialHistoryModal;
